export type ScheduleScope = 'all' | 'active' | 'selected';
export type ScheduleFrequency = 'hourly' | 'daily' | 'weekly' | 'monthly';

export interface ScheduleRunResult {
  ran: number;
  ok: number;
  failed: number;
  skipped: number;
  retried?: number;
  started_at: string;
  finished_at: string | null;
  errors: Array<{ forum: string; error: string }>;
  incomplete?: boolean;
  offset?: number;
  total?: number;
  message?: string;
  batch_finished_at?: string;
}

export interface ScrapeSchedule {
  id: string;
  name: string;
  scope: ScheduleScope;
  forum_ids: string[];
  frequency: ScheduleFrequency;
  run_at_hour: number;
  /** 0–59, Türkiye saati (yoksa 0) */
  run_at_minute?: number | null;
  run_at_weekday: number | null;
  run_at_day: number | null;
  enabled: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
  last_result: ScheduleRunResult | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export const SCOPE_LABELS: Record<ScheduleScope, string> = {
  all: 'Tüm forumlar',
  active: 'Aktif forumlar',
  selected: 'Seçili forumlar',
};

export const FREQUENCY_LABELS: Record<ScheduleFrequency, string> = {
  hourly: 'Saatlik',
  daily: 'Günlük',
  weekly: 'Haftalık',
  monthly: 'Aylık',
};

export const WEEKDAY_LABELS = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

const IST = 'Europe/Istanbul';

type IstParts = { y: number; m: number; d: number; h: number; min: number; w: number; ts: number };

/** Türkiye saati bileşenleri (runner ile aynı mantık). */
function istParts(from: Date = new Date()): IstParts {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: IST,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    weekday: 'short',
    hour12: false,
  });
  const p: Record<string, string> = {};
  for (const part of fmt.formatToParts(from)) {
    if (part.type !== 'literal') p[part.type] = part.value;
  }
  const wMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const y = Number(p.year);
  const m = Number(p.month);
  const d = Number(p.day);
  const h = Number(p.hour);
  const min = Number(p.minute);
  const w = wMap[p.weekday ?? 'Mon'] ?? 1;
  // ISO yerel → UTC (Europe/Istanbul sabit UTC+3)
  const ts = Date.UTC(y, m - 1, d, h - 3, min, 0);
  return { y, m, d, h, min, w, ts };
}

function istUtcIso(y: number, m: number, d: number, h: number, minute = 0): string {
  const min = Math.max(0, Math.min(59, minute));
  return new Date(Date.UTC(y, m - 1, d, h - 3, min, 0)).toISOString();
}

function addDays(y: number, m: number, d: number, delta: number): { y: number; m: number; d: number } {
  const dt = new Date(Date.UTC(y, m - 1, d + delta));
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

function clampMinute(minute: number | null | undefined): number {
  if (minute == null || !Number.isFinite(minute)) return 0;
  return Math.max(0, Math.min(59, Math.trunc(minute)));
}

/**
 * Bir sonraki çalışma zamanı (ISO). Saat/dakika Türkiye saatine göre;
 * kesin kaynak yine runner'dır (çalışmadan sonra sunucu yazar).
 */
export function computeNextRunIso(
  frequency: ScheduleFrequency,
  hour: number,
  weekday: number | null,
  day: number | null,
  minute: number | null | undefined = 0,
  from: Date = new Date()
): string {
  const now = istParts(from);
  const h = Math.max(0, Math.min(23, hour));
  const min = clampMinute(minute);

  if (frequency === 'hourly') {
    let candidate = istUtcIso(now.y, now.m, now.d, now.h, min);
    if (new Date(candidate).getTime() <= from.getTime()) {
      const nextH = now.h + 1;
      if (nextH >= 24) {
        const nd = addDays(now.y, now.m, now.d, 1);
        return istUtcIso(nd.y, nd.m, nd.d, 0, min);
      }
      return istUtcIso(now.y, now.m, now.d, nextH, min);
    }
    return candidate;
  }

  if (frequency === 'weekly') {
    const targetW = weekday === null ? 1 : Math.max(0, Math.min(6, weekday));
    const delta = (targetW - now.w + 7) % 7;
    let { y, m, d } = addDays(now.y, now.m, now.d, delta);
    let candidate = istUtcIso(y, m, d, h, min);
    if (new Date(candidate).getTime() <= from.getTime()) {
      ({ y, m, d } = addDays(y, m, d, 7));
      candidate = istUtcIso(y, m, d, h, min);
    }
    return candidate;
  }

  if (frequency === 'monthly') {
    const targetD = day === null ? 1 : Math.max(1, Math.min(28, day));
    let y = now.y;
    let m = now.m;
    let candidate = istUtcIso(y, m, targetD, h, min);
    if (new Date(candidate).getTime() <= from.getTime()) {
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
      candidate = istUtcIso(y, m, targetD, h, min);
    }
    return candidate;
  }

  // daily
  let { y, m, d } = { y: now.y, m: now.m, d: now.d };
  let candidate = istUtcIso(y, m, d, h, min);
  if (new Date(candidate).getTime() <= from.getTime()) {
    ({ y, m, d } = addDays(y, m, d, 1));
    candidate = istUtcIso(y, m, d, h, min);
  }
  return candidate;
}

function formatClock(hour: number, minute: number | null | undefined): string {
  return `${String(hour).padStart(2, '0')}:${String(clampMinute(minute)).padStart(2, '0')}`;
}

/** İnsan-okur özet: "Haftalık · Pazartesi · 03:15" gibi. */
export function describeSchedule(
  s: Pick<ScrapeSchedule, 'frequency' | 'run_at_hour' | 'run_at_minute' | 'run_at_weekday' | 'run_at_day'>
): string {
  const clock = formatClock(s.run_at_hour ?? 0, s.run_at_minute);
  if (s.frequency === 'hourly') {
    const m = clampMinute(s.run_at_minute);
    return m === 0 ? 'Saatlik (her saat başı)' : `Saatlik (her saatin ${String(m).padStart(2, '0')}. dakikası)`;
  }
  if (s.frequency === 'daily') return `Günlük · ${clock}`;
  if (s.frequency === 'weekly') {
    const wd = s.run_at_weekday === null ? 'Pazartesi' : WEEKDAY_LABELS[s.run_at_weekday] ?? '—';
    return `Haftalık · ${wd} · ${clock}`;
  }
  return `Aylık · ayın ${s.run_at_day ?? 1}. günü · ${clock}`;
}
